import {
  Button,
  Cell,
  FileInput,
  Image,
  Input,
  Radio,
  Spinner, Subheadline,
  Textarea
} from "@telegram-apps/telegram-ui";
import {ImageCropDialog} from "@/components/ImageCropDialog.tsx";
import {ChangeEvent, useEffect, useState} from "react";
import {createWishlist, deleteWishlist, updateWishlist, uploadImage} from "@/api/apiMethods.ts";
import {Image as ImageType, Wishlist} from "@/api/types.ts";
import {TbCamera, TbTextPlus} from "react-icons/tb";
import {useNavigation} from "@/providers/NavigationProvider.tsx";
import {useTranslation} from "react-i18next";
import {mainButton} from "@telegram-apps/sdk-react";
import {setOnClickMainButton} from "@/helpers/tgButtons.ts";
import {haptic} from "@/helpers/haptic.ts";

export const WishlistForm = (
  { wishlist, onSaved, onDeleted }:
  {
    wishlist?: Wishlist,
    onSaved?: (savedWishlist: Wishlist) => void,
    onDeleted?: (wishlistId: string) => void
  }
) => {
  const [ imageToCrop, setImageToCrop ] = useState<File | null>(null);
  const [ newImage, setNewImage ] = useState<ImageType | null>(wishlist?.cover || null);
  const [ isImageLoaded, setIsImageLoaded ] = useState<boolean>(newImage === null);
  const [ title, setTitle ] = useState<string>(wishlist?.title || "");
  const [ titleValidateError, setTitleValidateError ] = useState<boolean>(false);
  const [ description, setDescription ] = useState<string>(wishlist?.description || "");
  const [ access, setAccess ] = useState<"public"| "link" | "private">(wishlist?.access || "public");
  const [ isSaveInProgress, setIsSaveInProgress ] = useState<boolean>(false);
  const [ isDeleteInProgress, setIsDeleteInProgress ] = useState<boolean>(false);
  const { showSnackbar } = useNavigation();
  const { t } = useTranslation();

  const saveWishlist = async () => {
    if (!title) {
      haptic.notification('error');
      setTitleValidateError(true);
      showSnackbar("❌", "system.emptyTitle");
      return;
    }

    setIsSaveInProgress(true);
    const wishlistInfo = {
      title,
      description,
      cover: newImage,
      access: access,
      wishIds: null,
    }
    try {
      if (wishlist) {
        await updateWishlist(wishlist.id, wishlistInfo);
        const newWishlist = {...wishlist, ...wishlistInfo};
        if (onSaved) {
          onSaved(newWishlist);
        }
      } else {
        const createdWishlist = await createWishlist(wishlistInfo);
        if (onSaved) {
          onSaved(createdWishlist);
        }
      }
      haptic.notification('success');
    } catch (e) {
      haptic.notification('error');
      showSnackbar("❌", "system.unknowError");
    }

    setIsSaveInProgress(false);
  }

  useEffect(() => {
    setOnClickMainButton(async () => {
      await saveWishlist();
    })
    mainButton.setParams({
      isVisible: true,
      isLoaderVisible: isSaveInProgress,
      isEnabled: !isSaveInProgress || !isImageLoaded,
      text: !wishlist ? t('wishlist.action.create') : t('action.save'),
    });
  }, [saveWishlist, isSaveInProgress]);

  const onDelete = async () => {
    if (!wishlist) {
      return;
    }
    const doDelete = async (wishlist: Wishlist) => {
      try {
        await deleteWishlist(wishlist.id);
        haptic.notification('success');
        showSnackbar("🗑", "system.wishlistDeleted");
        if (onDeleted) {
          onDeleted(wishlist.id);
        }
      } catch (e) {
        haptic.notification('error');
        showSnackbar("❌", "system.unknowError");
      }
    };
    setIsDeleteInProgress(true);
    haptic.notification('warning');
    const isConfirmed = confirm("Удалить вишлист?");
    if (isConfirmed) {
      await doDelete(wishlist);
    }
    setIsDeleteInProgress(false);
  }

  const maxSizeImage = newImage?.sizes[newImage?.sizes.length - 1];
  let vertical = false;
  if (maxSizeImage && maxSizeImage.height >= maxSizeImage.width) {
    vertical = true
  }

  const onSelectImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    if (!target.files) {
      return;
    }
    const file = target.files[0];
    if (!file) {
      return;
    }
    haptic.impact('light');
    setImageToCrop(file);
  }

  const onCropped = async (fileBlob: Blob) => {
    setNewImage(null);
    setIsImageLoaded(false);
    const file = new File([fileBlob], "wishlist", { type: fileBlob.type });
    const newImage = await uploadImage(file);
    setNewImage(newImage);
    setImageToCrop(null);
    setIsImageLoaded(true);
    haptic.notification('success');
  }

  const onAccessOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    haptic.selection();
    setAccess(target.value as "public" | "link" | "private");
  }

  return (<div className={"flex flex-col gap-3"}>
    {imageToCrop && <ImageCropDialog onCrop={onCropped} file={imageToCrop} onClose={() => setImageToCrop(null)}/>}
    <div className={"flex flex-col items-center gap-0"}>
      <div className={"p-3 min-w-20 ml-5 mr-5 max-h-32 overflow-hidden flex justify-center items-center relative"}
           style={{height: maxSizeImage?.height + "px"}}>
        {newImage && <img height={vertical ? maxSizeImage?.height : undefined}
                          width={!vertical ? maxSizeImage?.width : undefined}
                          className={"object-cover max-h-32 max-w-full rounded-lg " + (isImageLoaded ? "visible" : "invisible")}
                          src={maxSizeImage?.link}
                          onLoad={() => setIsImageLoaded(true)}
                          alt={"Загрузка"}/>}
        {!newImage && <Image size={96} style={{backgroundColor: '#00000010'}}
                             fallbackIcon={<TbCamera size={50}/>}/>}
        {!isImageLoaded && <Spinner className={"image-loader"} size={"l"}/>}
      </div>

      <div style={{marginTop: "-15px", marginBottom: "-15px"}}>
        <FileInput label={t('wishlist.action.selectCover')}
                   onChange={onSelectImage}
                   accept="image/*,capture=camera"/>
      </div>
    </div>

    <Input placeholder={t('wishlist.placeholder.title')}
           before={<TbTextPlus size={20}/>}
           onFocus={() => setTitleValidateError(false)}
           onBlur={() => setTitleValidateError(!title)}
           status={titleValidateError ? "error" : "default"}
           value={title}
           onChange={(e) => setTitle(e.target.value)}/>
    <Textarea placeholder={t('wishlist.placeholder.description')} value={description} onChange={(e) => setDescription(e.target.value)} />

    <div className={"mb-3 mt-3"}>
      <Subheadline weight={"2"} className={"pl-5 mb-1 text-color-hint"}>{t('privacy.title')}</Subheadline>
        <Cell Component="label"
              className={"cell-compact-padding"}
              before={<Radio name="access"
                             value="public"
                             checked={access === "public"}
                             onChange={onAccessOptionChange}
              />}
              description={t('privacy.public.description')}>
          {t('privacy.public.title')}
        </Cell>
        <Cell Component="label"
              className={"cell-compact-padding"}
              before={<Radio name="access"
                             value="link"
                             onChange={onAccessOptionChange}
                             checked={access === "link"}
              />}
              description={t('privacy.link.description')}>{t('privacy.link.title')}</Cell>
        {/*<Cell Component="label"*/}
        {/*      className={"cell-compact-padding"}*/}
        {/*      before={<Radio name="access" value="private" onChange={onAccessOptionChange} checked={access === "private"}/>}*/}
        {/*      description={t('privacy.private.description')}>{t('privacy.private.title')}</Cell>*/}
    </div>
    {import.meta.env.MODE === 'development' && <Button disabled={isSaveInProgress}
            loading={isSaveInProgress}
            onClick={saveWishlist}
            stretched={true}>
      {!wishlist ? t('wishlist.action.create') : t('action.save')}
    </Button>}
    {wishlist && <Button stretched={true}
                     disabled={isDeleteInProgress}
                     loading={isDeleteInProgress}
                     onClick={onDelete}
                     mode={"plain"} size={"s"}>
      <div className={"text-color-destroy"}>{t('wishlist.action.delete')}</div>
    </Button>}
  </div>);
}
